<template>
    <div class="sec-cont">
        <Head :title="title"/>
        <div class="max-w-full bg-white rounded-md shadow p-6">
            <form @submit.prevent="submitForm">
                <h2 class="text-xl font-bold mb-4">Scheduler Details</h2>
                <div class="flex flex-wrap -mb-8 -mr-6">
                    <text-input class="pb-8 pr-6 w-full lg:w-1/3" v-model="title" :label="__('Title')" type="text" required/>
                    <div class="pb-8 pr-6 w-full lg:w-1/4">
                        <label class="form-label">{{ __('Event type') }}</label>
                        <div class="mt-2 mb-2 flex items-center">
                            <div class="mr-6">
                                <input type="radio" value="day" v-model="eventType" name="event_type" class="mr-2"
                                />{{ __('Day') }}
                            </div>
                            <div>
                                <input type="radio" value="event" v-model="eventType" name="event_type" class="mr-2"
                                />{{ __('Event') }}
                            </div>
                        </div>
                    </div>

                    <div class="pb-8 pr-6 w-full lg:w-1/3">
                        <!-- Single Date Picker for "Day" -->
                        <div v-if="eventType === 'day'" class="pb-8 pr-6 w-full">
                            <label class="form-label">{{ __('Select Date') }}</label>
                            <input type="date" v-model="singleDate" class="form-input mt-2 w-full"/>
                        </div>

                        <!-- From-To Date Picker for "Event" -->
                        <div v-else-if="eventType === 'event'" class="flex flex-wrap w-full">
                            <div class="pb-8 pr-6 w-full lg:w-1/2">
                                <label class="form-label">{{ __('From Date') }}</label>
                                <input type="date" v-model="fromDate" class="form-input mt-2 w-full"/>
                            </div>
                            <div class="pb-8 pr-6 w-full lg:w-1/2">
                                <label class="form-label">{{ __('To Date') }}</label>
                                <input type="date" v-model="toDate" class="form-input mt-2 w-full"/>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="pb-4 pr-6 w-full lg:w-1/3">
                    <multi-select-input2 id="assigned-to"
                                         :label="__('Class List')"
                                         v-model="selectedClass"
                                         :options="classList" required
                                         @dropdownClosed="onClassSelect"
                    />
                </div>
                <br>
                <div class="add-draft">
                    <div v-for="(e, eventIndex) in event.eventDetails" :key="eventIndex"
                         class="topic w-full flex flex-wrap -mr-6"
                    >
                        <div class="pb-4 pr-6 w-full lg:w-1/4">
                            <multi-select-input2 id="assigned-to"
                                                 :label="__('Draft List')"
                                                 v-model="e.selectedDraft"
                                                 :options="draftList" required
                            />
                        </div>
                        <div class="pb-4 pr-6 w-full lg:w-1/4">
                            <multi-select-input2 id="assigned-to"
                                                 :label="__('Class Selection')"
                                                 v-model="e.selectedClassForEvent"
                                                 :options="selectedClass"
                                                 required
                            />
                        </div>

                        <div
                            :class="{'pr-6 w-full': true, 'lg:w-1/3': eventType === 'event', 'lg:w-1/4': eventType === 'day'}"
                        >
                            <!-- Single Date Picker (Disabled for "Day") -->
                            <div v-if="eventType === 'day'" class="pb-8 pr-6 w-full">
                                <label class="form-label">{{ __('Select Date') }}</label>
                                <input type="date" v-model="singleDate" class="form-input mt-2 w-full"
                                       :disabled="true"
                                />
                            </div>

                            <!-- Restricted Date Picker for "Event" -->
                            <div v-else-if="eventType === 'event'" class="flex flex-wrap w-full">
                                <div class="pb-8 pr-6 w-full lg:w-1/2">
                                    <label class="form-label">{{ __('From Date') }}</label>
                                    <input type="date" v-model="e.fromDate" class="form-input mt-2 w-full"
                                           :min="fromDate" :max="toDate"
                                    />
                                </div>
                                <div class="pb-8 pr-6 w-full lg:w-1/2">
                                    <label class="form-label">{{ __('To Date') }}</label>
                                    <input type="date" v-model="e.toDate" class="form-input mt-2 w-full"
                                           :min="fromDate" :max="toDate"
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            :class="{'align-self-center pr-6 w-full ': true, 'lg:w-1/6': eventType === 'event', 'lg:w-1/4': eventType === 'day'}"
                        >
                            <button class="btn-indigo" @click="deleteTopic(eventIndex)">Delete</button>
                        </div>
                    </div>
                </div>

                <!-- Submit Button -->
                <div class="flex items-center justify-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <loading-button class="btn-indigo mx-auto" type="button" @click="addEventDetails">{{
                            __('Add Activity')
                        }}
                    </loading-button>
                    <loading-button class="btn-indigo mx-auto" type="submit">{{
                            __('Submit')
                        }}
                    </loading-button>
                </div>

            </form>
        </div>
    </div>
</template>

<script>
import {Head, Link} from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TextareaInput from '@/Shared/TextareaInput'
import AcademicQualifications from '@/Pages/Interview/AcademicQualifications'
import {QuillEditor} from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import MultiSelectInput2 from '@/Shared/MultiSelectInput2'
import axios from 'axios'
import {reactive} from 'vue'

export default {
    components: {
        FileInput,
        Head,
        Link,
        LoadingButton,
        SelectInput,
        TextInput,
        TextareaInput,
        AcademicQualifications,
        QuillEditor,
        MultiSelectInput2,
    },
    layout: Layout,
    props: {
        classList: {
            type: Array,
        },
        draftList: {
            required: true,
            type: Array,
        },
    },

    data() {
        return {
            event: reactive({
                selectedClassForEvent: null,
                eventDetails: [],  // This is the filtered list based on selected class
                selectedDraft: [],
                fromDate: null,
                toDate: null,

            }),
            classes: [{'id': 1, 'name': 'junior'}, {'id': 2, 'name': 'senior'}, {'id': 3, 'name': '1st'}, {
                'id': 1,
                'name': '2nd',
            }, {'id': 1, 'name': '3rd'}],
            eventType: 'day', // Default selection
            singleDate: null, // For "Day" event type
            fromDate: null,   // For "Event" event type
            toDate: null,     // For "Event" event type
            title: '',
            studentList: [],
            selectedClass: [],
            form: this.$inertia.form({
                title: null,
                eventType: null,
                singleDate: null,
                fromDate: null,
                toDate: null,
                selectedClass: null,
                eventDetails: [],
            }),
        }
    },
    watch: {
        // Reset dates when event type changes
        eventType(newType) {
            if (newType === 'day') {
                this.fromDate = null
                this.toDate = null
                // Apply single date to all event details
                this.event.eventDetails.forEach(detail => {
                    detail.singleDate = this.singleDate
                })
            } else if (newType === 'event') {
                this.singleDate = null
            }
        },
        // Sync all event details with the selected single date when eventType is "day"
        singleDate(newDate) {
            if (this.eventType === 'day') {
                this.event.eventDetails.forEach(detail => {
                    detail.singleDate = newDate
                })
            }
        },
        // Restrict event details date selection within the selected fromDate and toDate
        fromDate() {
            this.restrictEventDetailDates()
        },
        toDate() {
            this.restrictEventDetailDates()
        },
    },
    methods: {
        addEventDetails() {
            this.event.eventDetails.push({
                selectedDraft: [],
                selectedClassForEvent: [],
            })
        },
        deleteTopic(index) {
            this.event.eventDetails.splice(index, 1)
        },
        addImages(topicIndex, event) {
            const files = event.target.files
            Array.from(files).forEach((file) => {
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.topics[topicIndex].images.push(e.target.result)
                }
                reader.readAsDataURL(file)
            })
        },
        onClassSelect(selectedValue) {
            // Trigger the API call when selection changes
            axios.post(this.route('class.students.list'), {'classList': selectedValue}).then((response) => {
                if (response.data) {
                    this.studentList = response.data
                    this.event.eventDetails[0].filtredStudentList = response.data
                }
            }).catch((error) => {
                console.log(error)
            })

        },
        restrictEventDetailDates() {
            if (this.eventType === 'event') {
                this.event.eventDetails.forEach(detail => {
                    if (detail.fromDate && detail.fromDate < this.fromDate) {
                        detail.fromDate = this.fromDate
                    }
                    if (detail.toDate && detail.toDate > this.toDate) {
                        detail.toDate = this.toDate
                    }
                })
            }
        },
        submitForm() {

            this.form.title = this.title
            this.form.eventType = this.eventType
            this.form.singleDate = this.eventType === 'day' ? this.singleDate : null
            this.form.fromDate = this.eventType === 'event' ? this.fromDate : null
            this.form.toDate = this.eventType === 'event' ? this.toDate : null
            this.form.eventDetails = this.event.eventDetails.map(detail => ({
                selectedDraft: detail.selectedDraft,
                selectedClassForEvent: detail.selectedClassForEvent,
                singleDate: this.eventType === 'day' ? this.singleDate : null,
                fromDate: this.eventType === 'event' ? detail.fromDate : null,
                toDate: this.eventType === 'event' ? detail.toDate : null,
            }))

            this.form.post(this.route('add.scheduler.data')).then((response) => {
                this.form.reset()
                console.log(response)
                alert('data succesff')
            }).catch((error) => {
                console.log(error)
            })
        },
    },
    mounted() {
        // Initialize eventDetails if empty
        if (this.event.eventDetails.length === 0) {
            this.addEventDetails() // Adds a new event if the array is empty
        }
    },

}
</script>
